/**
 * Phase 1.7 — SectionHeader.
 *
 * Group label that lives between a FilterBar and a card grid. Tend's Crop
 * Library uses this to break the grid into Grazing / Green Manure / Mature
 * sections, each collapsible. We mirror that:
 *
 *   - title          required
 *   - count          optional integer pill
 *   - actions        optional right-aligned row (e.g., "View all")
 *   - collapsible    if true, a chevron toggles the section's children
 *   - defaultOpen    initial state for collapsible variant (default true)
 *
 * Group-by is fixed per module in Phase 1.7 — users don't pick the
 * grouping dimension, the page does. That decision lives upstream; this
 * component only renders.
 */

import { ChevronDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface SectionHeaderProps {
  title: string;
  count?: number;
  actions?: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  children?: ReactNode;
  className?: string;
}

export function SectionHeader({
  title,
  count,
  actions,
  collapsible,
  defaultOpen = true,
  children,
  className,
}: SectionHeaderProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  const renderChildren = !collapsible || open;

  return (
    <section className={cn('space-y-3', className)}>
      <header className="flex items-center justify-between border-b border-border pb-2">
        <button
          type="button"
          onClick={collapsible ? () => setOpen((v) => !v) : undefined}
          className={cn(
            'flex items-center gap-2',
            collapsible && 'group cursor-pointer focus-visible:outline-none',
          )}
          aria-expanded={collapsible ? open : undefined}
        >
          {collapsible ? (
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform',
                !open && '-rotate-90',
              )}
            />
          ) : null}
          <h2 className="font-display text-lg text-charcoal dark:text-charcoal-dark">{title}</h2>
          {typeof count === 'number' ? (
            <span className="rounded-full bg-surface-raised px-2 py-0.5 font-mono text-2xs text-muted-foreground dark:bg-surface-raised-dark">
              {count}
            </span>
          ) : null}
        </button>
        {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
      </header>
      {renderChildren ? children : null}
    </section>
  );
}
